import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppShell } from './components/AppShell';
import { ChunkLoadGuard } from './components/ChunkLoadGuard';
import AppErrorFallback from './components/AppErrorFallback';
import { useStore } from './store';

// Landing pages
const LandingPage = lazy(() => import('./pages/LandingPage'));
const Impressum = lazy(() => import('./pages/Impressum'));
const Datenschutz = lazy(() => import('./pages/Datenschutz'));
const Hilfe = lazy(() => import('./pages/Hilfe'));
const BlogIndex = lazy(() => import('./pages/BlogIndex'));

// App pages
const Login = lazy(() => import('./pages/Login'));
const MasterControl = lazy(() => import('./pages/MasterControl'));
const IGParasite = lazy(() => import('./pages/IGParasite'));
const ProfileCreator = lazy(() => import('./pages/ProfileCreator'));
const StrategyOS = lazy(() => import('./pages/StrategyOS'));
const SalesCrm = lazy(() => import('./pages/SalesCrm'));
const BusinessOutreach = lazy(() => import('./pages/BusinessOutreach'));
const UserManagement = lazy(() => import('./pages/UserManagement'));
const ActivityLog = lazy(() => import('./pages/ActivityLog'));
const PipelineEvents = lazy(() => import('./pages/PipelineEvents'));
const Profile = lazy(() => import('./pages/Profile'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const Integrations = lazy(() => import('./pages/Integrations'));
const SatelliteOS = lazy(() => import('./pages/SatelliteOS'));
const ContentVault = lazy(() => import('./pages/ContentVault'));
const TrendingMusic = lazy(() => import('./pages/TrendingMusic'));
const Reels = lazy(() => import('./pages/Reels'));
const ContactPool = lazy(() => import('./pages/ContactPool'));
const Fraud0Leads = lazy(() => import('./pages/Fraud0Leads'));
const Fraud0LeadsV2 = lazy(() => import('./pages/fraud0/Fraud0Leads'));
const LPAnalytics = lazy(() => import('./pages/LPAnalytics'));
const MindDashboard = lazy(() => import('./pages/mind/MindDashboard'));
const MindWorkshop = lazy(() => import('./pages/mind/MindWorkshop'));
const MindInbox = lazy(() => import('./pages/mind/MindInbox'));
const MindChallenge = lazy(() => import('./pages/mind/MindChallenge'));
const MindRetrospective = lazy(() => import('./pages/mind/MindRetrospective'));
const MindOnboarding = lazy(() => import('./pages/mind/MindOnboarding'));
const AdminStatLibraryPage = lazy(() => import('./pages/AdminStatLibraryPage'));
const ViewConfigAdmin = lazy(() => import('./pages/ViewConfigAdmin'));
const AdminTemplateStudioPage = lazy(() => import('./pages/AdminTemplateStudioPage'));
const LinkedInPage = lazy(() => import('./pages/LinkedInPage'));
const FirmsAdmin = lazy(() => import('./pages/FirmsAdmin'));
const Fraud0LinkedInPage = lazy(() => import('./pages/Fraud0LinkedInPage'));
const ExpansionAdvisor = lazy(() => import('./pages/ExpansionAdvisor'));
const CriticAudit = lazy(() => import('./pages/CriticAudit'));

function AuthGuard({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('token');
  if (!token) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AdminGuard({ children }: { children: React.ReactNode }) {
  const { user } = useStore();
  if (!user) return <Navigate to="/app" replace />;
  if (user.role !== 'admin') return <Navigate to="/app" replace />;
  return <>{children}</>;
}

function SuspenseWrap({ children }: { children: React.ReactNode }) {
  // ChunkLoadGuard catches "Failed to fetch dynamically imported module" errors
  // that happen when a stale browser tab tries to lazy-load a chunk whose hash
  // changed in a newer deploy. It auto-reloads once (with sessionStorage
  // loop-protection) so users never see the React-Router default error UI.
  return (
    <ChunkLoadGuard>
      <Suspense fallback={<div className="flex items-center justify-center h-screen text-text-muted">Loading...</div>}>
        {children}
      </Suspense>
    </ChunkLoadGuard>
  );
}

export const router = createBrowserRouter([
  // ===== LANDING (public) =====
  {
    path: '/',
    element: <SuspenseWrap><LandingPage lang="de" /></SuspenseWrap>,
  },
  {
    path: '/en',
    element: <SuspenseWrap><LandingPage lang="en" /></SuspenseWrap>,
  },
  {
    path: '/es',
    element: <SuspenseWrap><LandingPage lang="es" /></SuspenseWrap>,
  },
  {
    path: '/impressum',
    element: <SuspenseWrap><Impressum /></SuspenseWrap>,
  },
  {
    path: '/datenschutz',
    element: <SuspenseWrap><Datenschutz /></SuspenseWrap>,
  },
  {
    path: '/hilfe',
    element: <SuspenseWrap><Hilfe /></SuspenseWrap>,
  },
  {
    path: '/blog',
    element: <SuspenseWrap><BlogIndex /></SuspenseWrap>,
  },
  // i18n legal subpages
  {
    path: '/en/impressum',
    element: <SuspenseWrap><Impressum /></SuspenseWrap>,
  },
  {
    path: '/en/datenschutz',
    element: <SuspenseWrap><Datenschutz /></SuspenseWrap>,
  },
  {
    path: '/en/hilfe',
    element: <SuspenseWrap><Hilfe /></SuspenseWrap>,
  },
  {
    path: '/en/blog',
    element: <SuspenseWrap><BlogIndex /></SuspenseWrap>,
  },
  {
    path: '/es/impressum',
    element: <SuspenseWrap><Impressum /></SuspenseWrap>,
  },
  {
    path: '/es/datenschutz',
    element: <SuspenseWrap><Datenschutz /></SuspenseWrap>,
  },
  {
    path: '/es/hilfe',
    element: <SuspenseWrap><Hilfe /></SuspenseWrap>,
  },
  {
    path: '/es/blog',
    element: <SuspenseWrap><BlogIndex /></SuspenseWrap>,
  },

  // ===== APP (authenticated) =====
  {
    path: '/login',
    element: <SuspenseWrap><Login /></SuspenseWrap>,
  },
  {
    path: '/change-password',
    element: (
      <AuthGuard>
        <SuspenseWrap><ChangePassword /></SuspenseWrap>
      </AuthGuard>
    ),
  },
  {
    path: '/app',
    element: (
      <AuthGuard>
        <AppShell />
      </AuthGuard>
    ),
    errorElement: <AppErrorFallback />,
    children: [
      {
        index: true,
        element: <SuspenseWrap><MasterControl /></SuspenseWrap>,
      },
      {
        path: 'ig-parasite',
        element: <SuspenseWrap><IGParasite /></SuspenseWrap>,
      },
      {
        path: 'ig-parasite/:tab',
        element: <SuspenseWrap><IGParasite /></SuspenseWrap>,
      },
      {
        path: 'profile-creator',
        element: <SuspenseWrap><ProfileCreator /></SuspenseWrap>,
      },
      {
        path: 'profile-creator/:tab',
        element: <SuspenseWrap><ProfileCreator /></SuspenseWrap>,
      },
      {
        path: 'strategy-os',
        element: <SuspenseWrap><StrategyOS /></SuspenseWrap>,
      },
      {
        path: 'strategy-os/:tab',
        element: <SuspenseWrap><StrategyOS /></SuspenseWrap>,
      },
      {
        path: 'satellite-os',
        element: <SuspenseWrap><SatelliteOS /></SuspenseWrap>,
      },
      {
        path: 'satellite-os/:tab',
        element: <SuspenseWrap><SatelliteOS /></SuspenseWrap>,
      },
      {
        path: 'sales-crm',
        element: <SuspenseWrap><SalesCrm /></SuspenseWrap>,
      },
      {
        path: 'sales-crm/:tab',
        element: <SuspenseWrap><SalesCrm /></SuspenseWrap>,
      },
      {
        path: 'outreach',
        element: <SuspenseWrap><BusinessOutreach /></SuspenseWrap>,
      },
      {
        path: 'fraud0-linkedin',
        element: <SuspenseWrap><Fraud0LinkedInPage /></SuspenseWrap>,
      },
      // Alte Einzel-Routen → auf die zusammengeführte Seite umleiten (Altlinks/Bookmarks bleiben heil).
      {
        path: 'linkedin-tracking',
        element: <Navigate to="/app/fraud0-linkedin" replace />,
      },
      {
        path: 'linkedin-accounts',
        element: <Navigate to="/app/fraud0-linkedin" replace />,
      },
      {
        path: 'users',
        element: (
          <AdminGuard>
            <SuspenseWrap><UserManagement /></SuspenseWrap>
          </AdminGuard>
        ),
      },
      {
        path: 'admin/firms',
        element: (
          <AdminGuard>
            <SuspenseWrap><FirmsAdmin /></SuspenseWrap>
          </AdminGuard>
        ),
      },
      {
        path: 'activity',
        element: <SuspenseWrap><ActivityLog /></SuspenseWrap>,
      },
      {
        path: 'pipeline-events',
        element: (
          <AdminGuard>
            <SuspenseWrap><PipelineEvents /></SuspenseWrap>
          </AdminGuard>
        ),
      },
      {
        path: 'profile',
        element: <SuspenseWrap><Profile /></SuspenseWrap>,
      },
      {
        path: 'integrations',
        element: <SuspenseWrap><Integrations /></SuspenseWrap>,
      },
      {
        path: 'vault',
        element: <SuspenseWrap><ContentVault /></SuspenseWrap>,
      },
      {
        path: 'trending-music',
        element: <SuspenseWrap><TrendingMusic /></SuspenseWrap>,
      },
      {
        path: 'reels',
        element: <SuspenseWrap><Reels /></SuspenseWrap>,
      },
      {
        // Old standalone Reel Builder removed — redirect to Content Studio
        // where the embedded VideoBuilderPanel lives. Bookmarks stay valid.
        path: 'reel-builder',
        element: <Navigate to="/strategy-os/content-studio" replace />,
      },
      {
        path: 'reel-builder/:id',
        element: <Navigate to="/strategy-os/content-studio" replace />,
      },
      {
        path: 'contact-pool',
        element: <SuspenseWrap><ContactPool /></SuspenseWrap>,
      },
      {
        path: 'fraud0-leads',
        element: <SuspenseWrap><Fraud0LeadsV2 /></SuspenseWrap>,
      },
      {
        // Klassik-/Admin-Ansicht: Tabelle, Filter, Bulk, Daten-Ops, Economics-Kalibrierung
        path: 'fraud0-leads/klassik',
        element: <SuspenseWrap><Fraud0Leads /></SuspenseWrap>,
      },
      {
        // Alias auf die neue Swipe-UI (alte Bookmarks)
        path: 'fraud0-leads-v2',
        element: <SuspenseWrap><Fraud0LeadsV2 /></SuspenseWrap>,
      },
      {
        path: 'lp-analytics',
        element: (
          <AdminGuard>
            <SuspenseWrap><LPAnalytics /></SuspenseWrap>
          </AdminGuard>
        ),
      },
      {
        path: 'mind',
        element: <SuspenseWrap><MindDashboard /></SuspenseWrap>,
      },
      {
        path: 'mind/workshop',
        element: <SuspenseWrap><MindWorkshop /></SuspenseWrap>,
      },
      {
        path: 'mind/inbox',
        element: <SuspenseWrap><MindInbox /></SuspenseWrap>,
      },
      {
        path: 'mind/challenge',
        element: <SuspenseWrap><MindChallenge /></SuspenseWrap>,
      },
      {
        path: 'mind/retrospective',
        element: <SuspenseWrap><MindRetrospective /></SuspenseWrap>,
      },
      {
        path: 'mind/onboarding',
        element: <SuspenseWrap><MindOnboarding /></SuspenseWrap>,
      },
      {
        path: 'admin/stat-library',
        element: (
          <AdminGuard>
            <SuspenseWrap><AdminStatLibraryPage /></SuspenseWrap>
          </AdminGuard>
        ),
      },
      {
        path: 'admin/template-studio',
        element: (
          <AdminGuard>
            <SuspenseWrap><AdminTemplateStudioPage /></SuspenseWrap>
          </AdminGuard>
        ),
      },
      {
        path: 'admin/views',
        element: (
          <AdminGuard>
            <SuspenseWrap><ViewConfigAdmin /></SuspenseWrap>
          </AdminGuard>
        ),
      },
      {
        path: 'linkedin',
        element: <SuspenseWrap><LinkedInPage /></SuspenseWrap>,
      },
      {
        path: 'linkedin/:tab',
        element: <SuspenseWrap><LinkedInPage /></SuspenseWrap>,
      },
      {
        path: 'expansion',
        element: <SuspenseWrap><ExpansionAdvisor /></SuspenseWrap>,
      },
      {
        path: 'critic-audit',
        element: <SuspenseWrap><CriticAudit /></SuspenseWrap>,
      },
    ],
  },
]);
